<template>
    <div class="w-full flex flex-column align-items-center">
        <div class="card flex justify-center justify-content-center">
            <div class="user-image">
                <Image :src="imageSource" alt="Image" width="250" />
            </div>
        </div>

        <div class="card flex flex-wrap gap-3 align-items-center justify-content-center mt-6">

            <input type="file" ref="fileInput" accept="image/*" style="display: none" @change="onFileSelect" />

            <div class="file-select-container">
                <div class="file-select-field p-button p-component p-button-outlined p-button-secondary"
                    @click="triggerFileUpload">
                    <span class="select-text" :title="selectedFileName || 'Select a new photo'">
                        {{ apiCallInProgress ? 'Uploading...' : (selectedFileName || 'Select a new photo') }}
                    </span>
                </div>
            </div>
        </div>

        <Button label="Next" class="orange-button mt-6" @click="finalizeStep" :disabled="imageSource === logo || apiCallInProgress"/>
    </div>
</template>

<script setup>
import { ref, defineEmits, watchEffect } from "vue";
import Button from "primevue/button";
import Image from "primevue/image";
import { uploadProfileImage, generateSignedUrl } from '@/api/storage';
import { notifyError, notifyWarn } from '@/utils/notification';
import logo from '@/assets/blank-profile.png';

const emit = defineEmits(["profile-image-selected"]);
const apiCallInProgress = ref(false);
const fileInput = ref(null); // Reference to native input
const selectedFile = ref(null); // Hold the actual file object
const profileUrl = ref(null);
const imageSource = ref(logo);
const selectedFileName = ref('');

const triggerFileUpload = () => {
    fileInput.value.click();
};

const onFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
        selectedFile.value = file;
        selectedFileName.value = file.name;
        const reader = new FileReader();
        reader.onload = (e) => {
            imageSource.value = e.target.result;
        };
        reader.readAsDataURL(file);
        onUpload();
    }
};

const onUpload = async () => {
    if (!selectedFile.value) {
        notifyWarn("", "Please select a file to upload.");
        return;
    }
    try {
        apiCallInProgress.value = true;
        const formData = new FormData();
        formData.append('file', selectedFile.value);
        const res = await uploadProfileImage(formData);
        profileUrl.value = res.data.url;
    } catch (error) {
        notifyError("Error", "File upload failed. Please try again.");
    } finally {
        apiCallInProgress.value = false;
        selectedFile.value = null;
        fileInput.value.value = '';
    }
};

const finalizeStep = () => {
    if (selectedFile.value) {
        notifyWarn("Action Required", "Please wait for the image upload to complete.");
        return;
    }
    emit("profile-image-selected", profileUrl.value || null);
};

watchEffect(async () => {
    imageSource.value = profileUrl.value && profileUrl.value !== 'Service is not available in local environment' ?
        await generateSignedUrl(profileUrl.value) : logo;
});
</script>

<style scoped>
.user-image {
    margin-top: 5vh;
    border-radius: 10px;
    padding: 5px;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
}

.user-image :deep(img) {
    object-fit: cover;
    height: 250px;
    width: 250px;
}

.file-select-container {
    width: 300px;
    flex-shrink: 0;
}

.file-select-field {
    width: 100%;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    padding: 0 1rem;
}

.select-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    width: 100%;
    text-align: center;
    font-size: 0.9rem;
}
</style>